import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPublishedBlogs, getAllCategories, getAllTags, getBlogsByCategory } from '../utils/blogStorage';
import { FixedNav } from '../components';
import { arrow } from '../assets';


const SlidingTitle = ({ preTitle, mainText1, mainText2 }) => {
  const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false); 

  useEffect(() => {
    // 1. Capture the current element in a local variable
    const currentRef = ref.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.5 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      // 2. Use the local variable to ensure we unobserve the correct element
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []); // Empty array is fine here if you don't need to re-run on prop changes

  return (
    <div ref={ref} className="mb-16">
      <p className={`text-xs uppercase tracking-[4px] font-semibold text-[#ff6347] mb-4`}>
        {preTitle}
      </p>
      
      <h1 className="text-6xl md:text-7xl  font-grandslang leading-[0.9] -ml-1 flex items-baseline flex-wrap">
        {/* "Things I" - Placed higher in Z-index */}
        <span className={`relative z-10 bg-custom-background `}>
          {mainText1}
        </span>

        {/* The "Write" container to mask the entrance */}
        <span className="relative inline-block overflow-hidden py-2 -my-2 pl-4 lg:pl-6">
          <span
            className={`
              relative inline-block
              transition-all duration-1000 ease-out will-change-transform
              ${isVisible 
                ? 'translate-x-0 opacity-100' 
                : '-translate-x-full opacity-90'
              }
            `}
          >
            {mainText2}
          </span>
        </span>
      </h1>
    </div>
  );
};
export const AllBlogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [, setCategories] = useState([]);
  const [, setTags] = useState([]);
  const [selectedCategory] = useState('all');
  const [selectedTag] = useState('all');
  const [itemsToShow, setItemsToShow] = useState(3); // Pagination: show 9 initially

  const filterBlogs = useCallback(() => {
    let filtered = [...blogs];

    if (selectedCategory !== 'all') {
      filtered = getBlogsByCategory(selectedCategory);
    }

    if (selectedTag !== 'all') {
      filtered = filtered.filter(blog =>
        blog.tags && blog.tags.includes(selectedTag)
      );
    }

    setFilteredBlogs(filtered);
  }, [blogs, selectedCategory, selectedTag]);

  useEffect(() => {
    const publishedBlogs = getPublishedBlogs();
    setBlogs(publishedBlogs);
    setFilteredBlogs(publishedBlogs);
    setCategories(getAllCategories());
    setTags(getAllTags());
  }, []);

  useEffect(() => {
    filterBlogs();
    setItemsToShow(9); // Reset pagination when filters change
  }, [filterBlogs]);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleBlogClick = (blogId) => {
    navigate(`/insights/${blogId}`);
  };

  const visibleBlogs = filteredBlogs.slice(0, itemsToShow);
  const hasMore = itemsToShow < filteredBlogs.length;

  const loadMore = () => {
    setItemsToShow(prev => prev + 9);
  };

  return (
    <div className="min-h-screen bg-custom-background">
      <FixedNav />

      <section id="stories" className="py-28 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <SlidingTitle
          preTitle="Stories"
          mainText1="Things I"
          mainText2="Write"
        />

       {/* Filters
        {(categories.length > 0 || tags.length > 0) && (
          <div className="mb-16 grid md:grid-cols-2 gap-6">
            {categories.length > 0 && (
              <div>
                <label className={`block text-sm ${SUBTLE_TEXT_COLOR_CLASS} mb-2 uppercase tracking-wider font-medium`}>
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className={`w-full p-3 border ${BORDER_COLOR_CLASS} bg-white ${TEXT_COLOR_CLASS} transition-all focus:outline-none focus:ring-2 focus:ring-zinc-900`}
                >
                  <option value="all">All Categories</option>
                  {categories.map((cat, index) => (
                    <option key={index} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {tags.length > 0 && (
              <div>
                <label className={`block text-sm ${SUBTLE_TEXT_COLOR_CLASS} mb-2 uppercase tracking-wider font-medium`}>
                  Tag
                </label>
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className={`w-full p-3 border ${BORDER_COLOR_CLASS} bg-white ${TEXT_COLOR_CLASS} transition-all focus:outline-none focus:ring-2 focus:ring-zinc-900`}
                >
                  <option value="all">All Tags</option>
                  {tags.map((tag, index) => (
                    <option key={index} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )} */}

        {/* Blog Grid */}
        {filteredBlogs.length === 0 ? (
          <p className={` text-lg`}>
            No stories match your current filters.
          </p>
        ) : (
          <>
            {/* Increased vertical gap for that spacious feel */}
            <div className="grid gap-x-8 gap-y-8">
              {visibleBlogs.map((blog, i) => (
                <AnimatedBlogCard
                  key={blog.id}
                  id={i+1}
                  blog={blog}
                  handleBlogClick={handleBlogClick}
                  formatDate={formatDate}
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center mt-20">
                <button
                  onClick={loadMore}
                  className={`px-10 py-4 border border-zinc-900 bg-transparent hover:bg-zinc-900 hover:text-white transition-all duration-300 text-sm font-semibold uppercase tracking-wide`}
                >
                  Load More Stories ({filteredBlogs.length - itemsToShow} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </div>
      </section>
    </div>
  );
};


/**
 * AnimatedBlogCard
 * Optimized to ensure NO GAP between Title and Date at rest.
 */
const AnimatedBlogCard = ({ blog, handleBlogClick, formatDate, id }) => {
  if (!blog) return null;

  const date = formatDate ? formatDate(blog.createdAt, 'MMM , yyyy') : "No Date Provided";
  const excerpt = blog.excerpt || "";
  const tags = Array.isArray(blog.tags) ? blog.tags : [];

  return (
  <div onClick={() => handleBlogClick && handleBlogClick(blog.id)} key={blog.id} className="group flex flex-col gap-1 md:flex-row md:items-center justify-between py-12 border-b border-charcoal/10 hover:bg-white transition-colors px-4 cursor-pointer">
    <div className="flex md:flex-col justify-between md:justify-start gap-2 w-full md:w-32 pt-2">
        <span className="text-xs font-grandslang opacity-60 uppercase tracking-widest">0{id}</span>
        <span className="text-xs font-mono opacity-40 uppercase tracking-widest">{date}</span>
      </div>

      {/* Title and Description */}
      <div className="flex-1">
        <h3 className="text-xl font-semibold md:text-2xl tracking-widest font-grandslang uppercase 
          transition-transform duration-500
          group-hover:translate-x-2 group-hover:-skew-x-12 group-hover:text-[#ff6347]/90">
              {blog.title}
        </h3>
        
        <p className="max-w-xl opacity-70 mb-8 leading-relaxed font-light group-hover:opacity-40 transition-opacity duration-500">
          {excerpt}
        </p>

        {/* Tags - Cleaned up and consistent */}
        <div className="flex flex-wrap gap-3">
          {tags.map((tag, tIdx) => (
            <span 
              key={tIdx} 
              className="px-3 py-1 text-[10px] uppercase tracking-[0.2em] border border-white/10 rounded-full opacity-60 group-hover:opacity-100 group-hover:border-white/30 transition-all duration-500"
            >
              {tag}
            </span>
          ))}
  
        </div>
      </div>
      <img className='hidden md:block h-4' alt='arrow' src={arrow} />
      
      </div>
  );
};